/* eslint-disable no-fallthrough */
/* eslint-disable default-case */
import type { AttributeTarget } from '@hotjar/feedback/domain/Surveys/Survey/Survey';
import type { UserAttributeType } from '@hotjar/global-filters';
import {
  RadioNumberFilterOperation,
  RadioTextFilterOperation,
  RadioBooleanFilterOperation,
  RadioDateFilterOperation,
} from '@hotjar/global-filters';
import { UserAttributeKind } from '@hotjar/global-filters/src/domains/user-attributes/model/UserAttribute';

export const convertTargetToFilter = (target: AttributeTarget): UserAttributeType => {
  switch (target.rule_type) {
    case 'string': {
      switch (target.match_operation) {
        case 'exact': {
          return {
            name: target.name,
            kind: UserAttributeKind.String,
            value: {
              operation: target.negate
                ? RadioTextFilterOperation.IS_NOT
                : RadioTextFilterOperation.IS,
              value: target.pattern,
            },
          };
        }
        case 'unknown': {
          return {
            name: target.name,
            kind: UserAttributeKind.String,
            value: {
              operation: target.negate
                ? RadioTextFilterOperation.HAS_ANY_VALUE
                : RadioTextFilterOperation.IS_UNKNOWN,
            },
          };
        }
        case 'starts_with': {
          return {
            name: target.name,
            kind: UserAttributeKind.String,
            value: {
              operation: RadioTextFilterOperation.STARTS_WITH,
              value: target.pattern,
            },
          };
        }
        case 'ends_with': {
          return {
            name: target.name,
            kind: UserAttributeKind.String,
            value: {
              operation: RadioTextFilterOperation.ENDS_WITH,
              value: target.pattern,
            },
          };
        }
        case 'contains': {
          return {
            name: target.name,
            kind: UserAttributeKind.String,
            value: {
              operation: target.negate
                ? RadioTextFilterOperation.NOT_CONTAINS
                : RadioTextFilterOperation.CONTAINS,
              value: target.pattern,
            },
          };
        }
      }
    }
    case 'number': {
      switch (target.match_operation) {
        case 'exact': {
          return {
            name: target.name,
            kind: UserAttributeKind.Number,
            value: {
              operation: target.negate
                ? RadioNumberFilterOperation.IS_NOT
                : RadioNumberFilterOperation.IS_EXACTLY,
              value: Number(target.pattern),
            },
          };
        }
        case 'unknown': {
          return {
            name: target.name,
            kind: UserAttributeKind.Number,
            value: {
              operation: target.negate
                ? RadioNumberFilterOperation.HAS_ANY_VALUE
                : RadioNumberFilterOperation.IS_UNKNOWN,
            },
          };
        }
        case 'greater_than': {
          return {
            name: target.name,
            kind: UserAttributeKind.Number,
            value: {
              operation: RadioNumberFilterOperation.IS_GREATER_THAN,
              value: Number(target.pattern),
            },
          };
        }
        case 'less_than': {
          return {
            name: target.name,
            kind: UserAttributeKind.Number,
            value: {
              operation: RadioNumberFilterOperation.IS_LESS_THAN,
              value: Number(target.pattern),
            },
          };
        }
      }
    }
    case 'boolean': {
      switch (target.match_operation) {
        case 'exact': {
          return {
            name: target.name,
            kind: UserAttributeKind.Boolean,
            value: {
              operation:
                target.pattern === 'true'
                  ? RadioBooleanFilterOperation.IS_TRUE
                  : RadioBooleanFilterOperation.IS_FALSE,
            },
          };
        }
        case 'unknown': {
          return {
            name: target.name,
            kind: UserAttributeKind.Boolean,
            value: {
              operation: target.negate
                ? RadioBooleanFilterOperation.HAS_ANY_VALUE
                : RadioBooleanFilterOperation.IS_UNKNOWN,
            },
          };
        }
      }
    }
    case 'date': {
      switch (target.match_operation) {
        case 'unknown': {
          return {
            name: target.name,
            kind: UserAttributeKind.Date,
            value: {
              operation: target.negate
                ? RadioDateFilterOperation.HAS_ANY_VALUE
                : RadioDateFilterOperation.IS_UNKNOWN,
            },
          };
        }
        case 'greater_than': {
          return {
            name: target.name,
            kind: UserAttributeKind.Date,
            value: {
              operation: RadioDateFilterOperation.IS_AFTER,
              value: new Date(Number(target.pattern) * 1000),
            },
          };
        }
        case 'less_than': {
          return {
            name: target.name,
            kind: UserAttributeKind.Date,
            value: {
              operation: RadioDateFilterOperation.IS_BEFORE,
              value: new Date(Number(target.pattern) * 1000),
            },
          };
        }
        case 'exact_days_ago': {
          return {
            name: target.name,
            kind: UserAttributeKind.Date,
            value: {
              operation: RadioDateFilterOperation.IS_EXACTLY_DAYS_AGO,
              value: Number(target.pattern),
            },
          };
        }
        case 'more_than_days_ago': {
          return {
            name: target.name,
            kind: UserAttributeKind.Date,
            value: {
              operation: RadioDateFilterOperation.IS_MORE_THAN_DAYS_AGO,
              value: Number(target.pattern),
            },
          };
        }
        case 'less_than_days_ago': {
          return {
            name: target.name,
            kind: UserAttributeKind.Date,
            value: {
              operation: RadioDateFilterOperation.IS_LESS_THAN_DAYS_AGO,
              value: Number(target.pattern),
            },
          };
        }
        case 'exact_date': {
          return {
            name: target.name,
            kind: UserAttributeKind.Date,
            value: {
              operation: RadioDateFilterOperation.IS_ON,
              value: new Date(Number(target.pattern) * 1000),
            },
          };
        }
      }
    }
  }
};
